import { Type as t } from '@sinclair/typebox';
import { BaseResponseSchema, PaginatedResponseSchema } from './base';
import { UserRelationship, UserRelationshipSchema } from './userTypes';

export enum InboxMessageType {
  ASK_SENT = 'ASK_SENT',
  ASK_RECEIVED = 'ASK_RECEIVED',
  ASK_FEEDBACK_SENT = 'ASK_FEEDBACK_SENT',
  ASK_FEEDBACK_RECEIVED = 'ASK_FEEDBACK_RECEIVED',
  SEARCH_SENT = 'SEARCH_SENT',
  NOTE_ADDED = 'NOTE_ADDED',
  CONNECTION_REQUEST_RECEIVED = 'CONNECTION_REQUEST_RECEIVED',
  CONNECTION_ESTABLISHED = 'CONNECTION_ESTABLISHED',
  PEOPLE_YOU_MAY_KNOW = 'PEOPLE_YOU_MAY_KNOW',
  LOVE_RECEIVED_FROM_CONNECTION = 'LOVE_RECEIVED_FROM_CONNECTION',
  LOVE_RECEIVED_FROM_PUBLIC = 'LOVE_RECEIVED_FROM_PUBLIC',
  TRENDING_TOPIC = 'TRENDING_TOPIC',
  INVITE_SLOT_GRANTED = 'INVITE_SLOT_GRANTED',
  ANSWER_APPROVED = 'ANSWER_APPROVED',
}

export enum InboxEntityType {
  ASK = 'ASK',
  MULTI_ASK = 'MULTI_ASK',
  NOTE = 'NOTE',
  CONNECTION = 'CONNECTION', // entity ID is user ID
  PEOPLE_YOU_MAY_KNOW = 'PEOPLE_YOU_MAY_KNOW',
  USER = 'USER',
  TOPIC = 'TOPIC',
  INVITE = 'INVITE',
}

export type InboxMessageDataType = DefaultInboxMessageData | ConnectionRequestInboxMessageData;

export interface DefaultInboxMessageData {
  thumbnailUrl?: string;
  title?: string;
  /** @deprecated */
  subtitle?: string;
  secondaryEntityInfo?: {
    entityType: InboxEntityType;
    entityId: string;
  };
  displayUser?: UserBaseModel;
}

interface ConnectionRequestInboxMessageData {
  thumbnailUrl: string;
  title: string;
  /** @deprecated add here to avoid breaking changes */
  subtitle?: string;
  relationship: UserRelationship;
}

export interface TrendingTopicInboxMessageData extends DefaultInboxMessageData {
  askCount: number;
  maxPathAskCount?: number;
}

export enum InboxFeedType {
  NEW = 'NEW',
  RECENT = 'RECENT',
}

// in sync with ios UserBaseModel in User.swift
export interface UserBaseModel {
  id: string;
  uniqueId: string | null;
  displayName: string | null;
  profileImageUrl: string | null;
}

// TypeBox schemas for API responses
export const InboxMessageTypeSchema = t.Enum(InboxMessageType);

export const InboxEntityTypeSchema = t.Enum(InboxEntityType);

export const UserBaseModelSchema = t.Object({
  id: t.String(),
  uniqueId: t.Union([t.String(), t.Null()]),
  displayName: t.Union([t.String(), t.Null()]),
  profileImageUrl: t.Union([t.String(), t.Null()]),
});

export const DefaultInboxMessageDataSchema = t.Object({
  thumbnailUrl: t.Optional(t.String()),
  title: t.Optional(t.String()),
  subtitle: t.Optional(t.String()),
  secondaryEntityInfo: t.Optional(
    t.Object({
      entityType: InboxEntityTypeSchema,
      entityId: t.String(),
    }),
  ),
  displayUser: t.Optional(UserBaseModelSchema),
});

export const ConnectionRequestInboxMessageDataSchema = t.Object({
  thumbnailUrl: t.String(),
  title: t.String(),
  subtitle: t.Optional(t.String()),
  relationship: UserRelationshipSchema,
});

export const TrendingTopicInboxMessageDataSchema = t.Intersect([
  DefaultInboxMessageDataSchema,
  t.Object({
    askCount: t.Number(),
    askCountForDedup: t.Optional(t.Number()),
  }),
]);

export const InboxMessageDataSchema = t.Union([
  DefaultInboxMessageDataSchema,
  ConnectionRequestInboxMessageDataSchema,
  TrendingTopicInboxMessageDataSchema,
]);

export const InboxMessageSchema = t.Object({
  id: t.String(),
  userId: t.String(),
  type: InboxMessageTypeSchema,
  entityType: InboxEntityTypeSchema,
  entityId: t.String(),
  data: t.Optional(InboxMessageDataSchema),
  seenStatus: t.Optional(t.String()),
  updatedAt: t.String(),
  createdAt: t.String(),
});

export type InboxMessage = typeof InboxMessageSchema.static;

export const PaginatedInboxMessagesSchema = PaginatedResponseSchema(InboxMessageSchema);

export type PaginatedInboxMessages = typeof PaginatedInboxMessagesSchema.static;

export const GetInboxMessageByIdResponseSchema = BaseResponseSchema(InboxMessageSchema);
export type GetInboxMessageByIdResponse = typeof GetInboxMessageByIdResponseSchema.static;
